/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Grid, IconButton, Snackbar } from "@mui/material";
import { ArrowBack, Extension, Share, Webhook, Chat } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import IntegrationsSettings from "components/SettingsComponents/IntegrationsSettings";
import { useAuth } from "auth/Auth";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const TAB_ITEMS = [
  { key: "webhook", label: "Webhooks", icon: <Webhook fontSize="small" /> },
  { key: "api", label: "API Integrations", icon: <Share fontSize="small" /> },
  { key: "social", label: "Social", icon: <Chat fontSize="small" /> },
];

const SECTION_ALIASES = {
  webhooks: "webhook",
  webhook: "webhook",
  api: "api",
  social: "social",
};

function resolveSection(raw) {
  const normalized = (raw || "webhook").trim().toLowerCase();
  return SECTION_ALIASES[normalized] || "webhook";
}

export default function IntegrationsSettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const [searchParams, setSearchParams] = useSearchParams();
  const { axiosInstance } = useAuth();

  const sectionFromUrl = resolveSection(searchParams.get("section"));
  const [activeSection, setActiveSection] = useState(sectionFromUrl);
  const tabRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const [companyInfo, setCompanyInfo] = useState(null);
  const [integrationData, setIntegrationData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setActiveSection(sectionFromUrl);
  }, [sectionFromUrl]);

  useEffect(() => {
    const index = TAB_ITEMS.findIndex((tab) => tab.key === activeSection);
    const currentTab = tabRefs.current[index >= 0 ? index : 0];
    if (currentTab) {
      setIndicator({
        left: currentTab.offsetLeft,
        width: currentTab.offsetWidth,
      });
    }
  }, [activeSection]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get("/company/info");
        if (!cancelled) setCompanyInfo(response.data);
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance]);

  const handleTabChange = useCallback(
    (sectionKey) => {
      setActiveSection(sectionKey);
      setSearchParams({ section: sectionKey }, { replace: true });
    },
    [setSearchParams]
  );

  const handleSave = async () => {
    if (!integrationData) return;
    if (!companyInfo?.company_name) {
      setToast({
        severity: "error",
        message: "Company details are still loading. Try again in a moment.",
      });
      return;
    }
    setIsSaving(true);
    try {
      await axiosInstance.post("/company-api-keys/", {
        company_name: companyInfo.company_name,
        maps_api_key: integrationData.googleMapsApiKey,
        ai_api_key: integrationData.aiApiKey,
        gst_api_key: integrationData.gstApiKey,
        webhooks: integrationData.webhooks,
      });
      setToast({ severity: "success", message: "Integration settings saved." });
      setCompanyInfo((prev) =>
        prev ? { ...prev, ai_api_key: integrationData.aiApiKey } : prev
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: "error",
        message:
          typeof detail === "string"
            ? detail
            : "Could not save integration settings.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const activeTabIndex = Math.max(
    0,
    TAB_ITEMS.findIndex((tab) => tab.key === activeSection)
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {isV2 ? (
              <PageHeader title="Integrations" summary="Configure third-party integrations, API keys and messaging channels" />
            ) : (
            <MDBox display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate("/settings")} sx={{ mr: -0.5 }}>
                <ArrowBack />
              </IconButton>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "#d7e3fc",
                }}
              >
                <Extension sx={{ color: "info.main" }} />
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  Integrations
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  Configure third-party integrations, API keys and messaging channels
                </MDTypography>
              </MDBox>
            </MDBox>
            )}
          </Grid>

          <Grid item xs={12}>
            <MDBox
              bgColor="white"
              position="relative"
              display="flex"
              sx={{
                border: "1px solid",
                borderRadius: "5px",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              {TAB_ITEMS.map((tab, index) => {
                const isSelected = activeTabIndex === index;
                return (
                  <MDBox
                    key={tab.key}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    onClick={() => handleTabChange(tab.key)}
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 2,
                      cursor: "pointer",
                      userSelect: "none",
                      color: isSelected ? "info.main" : "text.secondary",
                      transition: "color 0.3s ease",
                      "& svg": { fontSize: "1.125rem" },
                    }}
                  >
                    {tab.icon}
                    <MDTypography
                      variant="button"
                      fontWeight={isSelected ? "bold" : "regular"}
                      sx={{ color: "inherit", transition: "color 0.3s ease" }}
                    >
                      {tab.label}
                    </MDTypography>
                  </MDBox>
                );
              })}
              <MDBox
                sx={{
                  position: "absolute",
                  bottom: 0,
                  height: "2px",
                  backgroundColor: "info.main",
                  left: `${indicator.left}px`,
                  width: `${indicator.width}px`,
                  transition: "left 0.3s ease, width 0.3s ease",
                }}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12}>
            <MDBox
              bgColor="white"
              sx={{
                borderRadius: "12px",
                p: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <IntegrationsSettings
                fullPageLayout
                selectedSection={activeSection}
                onSave={handleSave}
                isSaving={isSaving}
                companyInfo={companyInfo}
                onDataChange={setIntegrationData}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </DashboardLayout>
  );
}
